import { useEffect, useMemo, useState } from 'react';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import {
  App,
  Button,
  Input,
  Space,
  Tag,
  Tooltip,
  Typography,
  type TableColumnsType,
  type TableProps,
} from 'antd';
import { BadgePlus, Edit3, Plus, RefreshCw, Search, Trash2 } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { iamApi as iamHttpApi, type IamPolicySearchRecord } from '@/api/iam.api';
import ProTable from '@/components/pro/ProTable';
import { useDeleteIamPolicy } from '@/pages/iam/composables/useIamSupportData';
import { debounce, fmtTime } from '@/utils/utils';
import GrantPolicyDrawer from './components/GrantPolicyDrawer';
import './policies.css';

const PAGE_SIZE = 20;
type PolicySortField = 'authorization_count' | 'created_at';

function countTag(count: number, label: string, color = 'blue') {
  return (
    <Tag bordered={false} color={count > 0 ? color : 'default'}>
      {count} {label}
    </Tag>
  );
}

/** IAM 策略时间戳以秒为单位；仅在展示时转换，不改写接口数据。 */
function formatPolicyTime(timestamp: number): string {
  return fmtTime(new Date(timestamp * 1000).toISOString());
}

export default function PolicyList() {
  const { message, modal } = App.useApp();
  const navigate = useNavigate();
  const [keyword, setKeyword] = useState('');
  const [queryKeyword, setQueryKeyword] = useState('');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(PAGE_SIZE);
  const [orderBy, setOrderBy] = useState<PolicySortField | `-${PolicySortField}`>('-created_at');
  const [grantingPolicy, setGrantingPolicy] = useState<IamPolicySearchRecord>();
  const [checkingPolicyId, setCheckingPolicyId] = useState<string>();

  const updateQueryKeyword = useMemo(
    () =>
      debounce((value: string) => {
        setQueryKeyword(value);
        setPage(1);
      }, 300),
    []
  );

  useEffect(() => () => updateQueryKeyword.cancel(), [updateQueryKeyword]);

  const listParams = useMemo(
    () => ({
      limit: String(pageSize),
      offset: String((page - 1) * pageSize),
      order_by: orderBy,
      ...(queryKeyword.trim() ? { search: queryKeyword.trim() } : {}),
    }),
    [page, pageSize, queryKeyword, orderBy]
  );
  const {
    data,
    error,
    isLoading,
    isFetching: isSearching,
    refetch: refetchList,
  } = useQuery({
    queryKey: ['iam', 'policies', 'list', listParams] as const,
    queryFn: async () => {
      const response = await iamHttpApi.searchPolicies(listParams);
      if (response.code !== 0) {
        throw new Error(response.message || '搜索权限策略失败');
      }
      return response;
    },
    placeholderData: keepPreviousData,
    refetchOnWindowFocus: false,
  });
  const deletePolicy = useDeleteIamPolicy();

  const policies = useMemo(() => data?.data.policies ?? [], [data]);
  const listErrorMessage = error ? (error instanceof Error ? error.message : String(error)) : null;

  const confirmRemovePolicy = async (row: IamPolicySearchRecord) => {
    if (checkingPolicyId) return;

    setCheckingPolicyId(row.policy_id);
    try {
      const [users, userGroups] = await Promise.all([
        iamHttpApi
          .searchPolicyGrants({
            identity_type: 'user',
            limit: 1,
            offset: '0',
            policy_id: row.policy_id,
          })
          .then((response) => {
            if (response.code !== 0) {
              throw new Error(response.message || '查询用户策略授权失败');
            }
            return response;
          }),
        iamHttpApi
          .searchPolicyGrants({
            identity_type: 'user_group',
            limit: 1,
            offset: '0',
            policy_id: row.policy_id,
          })
          .then((response) => {
            if (response.code !== 0) {
              throw new Error(response.message || '查询用户组策略授权失败');
            }
            return response;
          }),
      ]);

      modal.confirm({
        title: '删除策略',
        content: `将解除 ${users.data.total} 个用户、${userGroups.data.total} 个用户组的关联授权，并保留撤销审计记录。`,
        okText: '解除关联并删除',
        okType: 'danger',
        async onOk() {
          try {
            await deletePolicy.mutateAsync(row.policy_id);
            message.success('策略已删除');
          } catch {
            // 请求错误由全局请求层统一提示。
          }
        },
      });
    } catch {
      // 点击事件以 fire-and-forget 方式调用，需消费 rejection，错误提示由请求层处理。
    } finally {
      setCheckingPolicyId(undefined);
    }
  };

  const refresh = () => {
    void refetchList();
  };

  const columns: TableColumnsType<IamPolicySearchRecord> = [
    {
      title: '策略',
      key: 'policy',
      render: (_, row) => (
        <div className="iam-policy-name-cell">
          <Space>
            <Typography.Text strong>{row.name}</Typography.Text>
            <Tag color={row.type === 'system' ? 'blue' : 'purple'} bordered={false}>
              {row.type === 'system' ? '系统策略' : '自定义'}
            </Tag>
          </Space>
          <Typography.Text type="secondary">{row.description}</Typography.Text>
        </div>
      ),
    },
    {
      title: '授权次数',
      key: 'authorization_count',
      width: 140,
      sorter: true,
      sortOrder:
        orderBy === 'authorization_count'
          ? 'ascend'
          : orderBy === '-authorization_count'
            ? 'descend'
            : null,
      sortDirections: ['descend', 'ascend', 'descend'],
      render: (_, r) => countTag(r.authorization_count, '次', 'geekblue'),
    },
    {
      title: '创建时间',
      key: 'created_at',
      width: 160,
      sorter: true,
      sortOrder: orderBy === 'created_at' ? 'ascend' : orderBy === '-created_at' ? 'descend' : null,
      sortDirections: ['descend', 'ascend', 'descend'],
      render: (_, r) => formatPolicyTime(r.created_at),
    },
    {
      title: '操作',
      width: 144,
      fixed: 'right',
      render: (_, row) => {
        const editable = row.type === 'custom';
        return editable ? (
          <Space size={0} onClick={(e) => e.stopPropagation()}>
            <Tooltip title="新增授权">
              <Button
                type="text"
                aria-label={`为 ${row.name} 新增授权`}
                icon={<BadgePlus size={14} />}
                onClick={() => setGrantingPolicy(row)}
              />
            </Tooltip>
            <Tooltip title="编辑策略">
              <Button
                type="text"
                aria-label={`编辑策略 ${row.name}`}
                icon={<Edit3 size={15} />}
                onClick={() => navigate(`/iam/policies/${row.policy_id}/edit`)}
              />
            </Tooltip>
            <Tooltip title="删除策略">
              <Button
                type="text"
                danger
                aria-label={`删除策略 ${row.name}`}
                aria-busy={checkingPolicyId === row.policy_id}
                disabled={checkingPolicyId !== undefined && checkingPolicyId !== row.policy_id}
                loading={checkingPolicyId === row.policy_id}
                icon={<Trash2 size={15} />}
                onClick={() => void confirmRemovePolicy(row)}
              />
            </Tooltip>
          </Space>
        ) : (
          <span onClick={(e) => e.stopPropagation()}>
            <Tooltip title="新增授权">
              <Button
                type="text"
                aria-label={`为 ${row.name} 新增授权`}
                icon={<BadgePlus size={14} />}
                onClick={() => setGrantingPolicy(row)}
              />
            </Tooltip>
          </span>
        );
      },
    },
  ];

  const handleTableChange: TableProps<IamPolicySearchRecord>['onChange'] = (
    _pagination,
    _filters,
    sorter,
    extra
  ) => {
    if (extra.action !== 'sort') return;
    const activeSorter = Array.isArray(sorter) ? sorter[0] : sorter;
    const field = activeSorter?.columnKey;
    if (!activeSorter?.order || (field !== 'authorization_count' && field !== 'created_at')) {
      return;
    }
    setOrderBy(activeSorter.order === 'ascend' ? field : `-${field}`);
    setPage(1);
  };

  return (
    <div className="pp-page iam-page">
      <header className="pp-head">
        <h2>权限策略</h2>
        <Space>
          <Button icon={<RefreshCw size={14} />} onClick={refresh}>
            刷新
          </Button>
          <Button
            type="primary"
            icon={<Plus size={14} />}
            onClick={() => navigate('/iam/policies/create')}
          >
            新建策略
          </Button>
        </Space>
      </header>

      <div className="iam-policy-toolbar">
        <Input
          aria-label="搜索关键词"
          className="iam-policy-search"
          allowClear
          prefix={<Search size={14} />}
          placeholder="搜索策略名称 / 策略 ID"
          value={keyword}
          onChange={(e) => {
            const value = e.target.value;
            setKeyword(value);
            updateQueryKeyword(value);
          }}
        />
      </div>

      <ProTable<IamPolicySearchRecord>
        data={policies}
        loading={isLoading}
        tableLoading={isSearching && !isLoading}
        error={listErrorMessage}
        columns={columns}
        onChange={handleTableChange}
        rowKey="policy_id"
        onRetry={refresh}
        pagination={{
          current: page,
          pageSize,
          total: data?.data.total ?? 0,
          showSizeChanger: true,
          pageSizeOptions: [10, 20, 50, 100],
          showTotal: (count) => `共 ${count} 条`,
          onChange: (nextPage, nextPageSize) => {
            setPage(nextPage);
            setPageSize(nextPageSize);
          },
        }}
        onRow={(row) => ({
          onClick: () => navigate(`/iam/policies/${row.policy_id}`),
          style: { cursor: 'pointer' },
        })}
      />

      <GrantPolicyDrawer
        open={!!grantingPolicy}
        policy={
          grantingPolicy ? { id: grantingPolicy.policy_id, name: grantingPolicy.name } : undefined
        }
        onClose={() => setGrantingPolicy(undefined)}
      />
    </div>
  );
}
